import React, { useState, useEffect, useCallback } from 'react';
import { message, Select, Spin, Pagination, Dropdown, Button, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { HomeOutlined, MoreOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { knowledgeBaseService, KnowledgeBase } from '../../services/knowledgeBase/knowledgeBaseService';
import { PRESET_CATEGORIES } from './presets';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import '../MemberManagement.css';
import '../../styles/resourceManagement.css';
import '../../styles/literature-lims.css';
import '../../styles/literature-stitch.css';

const SORT_OPTIONS: { value: string; label: string }[] = [
  { value: 'createdAt-desc', label: '最新创建' },
  { value: 'createdAt-asc', label: '最早创建' },
  { value: 'viewCount-desc', label: '阅读量最高' },
  { value: 'likeCount-desc', label: '点赞最多' },
];

const { Option } = Select;

const KB_ACCESS_TABS = [
  { key: 'public' as const, label: '公开（全员可见）', icon: 'public' as const },
  { key: 'private' as const, label: '私有（仅本人）', icon: 'lock' as const },
  { key: 'restricted' as const, label: '受限访问（指定成员）', icon: 'groups' as const }
];

const statusLabel = (status: string) => {
  const m: Record<string, string> = { draft: '草稿', published: '已发布', archived: '已归档' };
  return m[status] || status;
};

const kbStatusPill = (status: string) => {
  const cls =
    status === 'published'
      ? 'rm-kb-pill rm-kb-pill--published'
      : status === 'archived'
        ? 'rm-kb-pill rm-kb-pill--archived'
        : 'rm-kb-pill rm-kb-pill--draft';
  return <span className={cls}>{statusLabel(status)}</span>;
};

function formatKbRelativeTime(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const t = d.getTime();
  if (Number.isNaN(t)) return '—';
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '刚刚';
  if (mins < 60) return `${mins} 分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} 天前`;
  return d.toLocaleDateString('zh-CN', {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });
}

/** 列表卡片摘要：优先摘要，否则从正文剥 HTML */
function kbListCardSnippet(record: KnowledgeBase): string {
  const raw = (record.summary || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (raw) return raw.slice(0, 220);
  const content = (record.content || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return content.slice(0, 220);
}

const KnowledgeBaseList: React.FC = () => {
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 6,
    total: 0,
  });
  const [filters, setFilters] = useState({
    keyword: '',
    category: '',
    status: '',
    sortBy: 'createdAt',
    sortOrder: 'desc' as 'asc' | 'desc',
    accessTab: 'public' as 'public' | 'private' | 'restricted',
  });
  const [searchDraft, setSearchDraft] = useState('');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const q = searchParams.get('q');
    if (q && q.trim()) {
      const v = q.trim();
      setFilters((prev) => ({ ...prev, keyword: v }));
      setSearchDraft(v);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
    const tab = searchParams.get('tab');
    if (tab === 'public' || tab === 'private' || tab === 'restricted') {
      setFilters((prev) => ({ ...prev, accessTab: tab }));
    }
  }, [searchParams]);

  useEffect(() => {
    setSearchDraft(filters.keyword);
  }, [filters.keyword]);

  const fetchKnowledgeBases = useCallback(async () => {
    setLoading(true);
    try {
      const result = await knowledgeBaseService.getList({
        page: pagination.page,
        limit: pagination.limit,
        keyword: filters.keyword || undefined,
        category: filters.category || undefined,
        status: filters.status || undefined,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        accessTab: filters.accessTab,
      });
      setKnowledgeBases(result.knowledgeBases);
      setPagination((prev) => ({
        ...prev,
        total: result.pagination.total,
      }));
    } catch (error: any) {
      message.error(error.message || '获取知识库列表失败');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchKnowledgeBases();
  }, [fetchKnowledgeBases]);

  const commitSearch = () => {
    const v = searchDraft.trim();
    setFilters((prev) => {
      const next = { ...prev, keyword: v };
      setSearchParams((sp) => {
        const u = new URLSearchParams(sp);
        if (v) u.set('q', v);
        else u.delete('q');
        u.set('tab', next.accessTab);
        return u;
      });
      return next;
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('确定要删除该文档吗？删除后不可恢复。')) return;
    try {
      await knowledgeBaseService.delete(id);
      message.success('删除成功');
      fetchKnowledgeBases();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  return (
    <div className="page-container full-height members-lims kb-lims literature-lims-module">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>知识库</span>
      </nav>

      <header className="mm-page-head">
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 w-100">
          <div>
            <h1 className="mm-title">知识库</h1>
            <p className="mm-subtitle">检索与维护实验室文档、视频与附件，界面与成员与物资模块一致。</p>
          </div>
          <button type="button" className="mm-btn-primary" onClick={() => navigate('/knowledge-base/new')}>
            <LimsNavIcon name="library_add" sizePx={20} />
            新建文档
          </button>
        </div>
      </header>

      <div className="content-area px-1 px-md-2">
        <div className="am-main-tabs">
          {KB_ACCESS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`am-main-tab${filters.accessTab === t.key ? ' am-main-tab--active' : ''}`}
              onClick={() => {
                setFilters((prev) => ({ ...prev, accessTab: t.key }));
                setPagination((prev) => ({ ...prev, page: 1 }));
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('tab', t.key);
                  return next;
                });
              }}
            >
              <span className="d-inline-flex align-items-center gap-1">
                <LimsNavIcon name={t.icon} sizePx={18} />
                {t.label}
              </span>
            </button>
          ))}
        </div>
        <p className="small text-muted mb-3" style={{ marginTop: '-8px' }}>
          {filters.accessTab === 'public' && '展示全员可见的公开文档。'}
          {filters.accessTab === 'private' && '仅展示您本人创建的私有文档，他人无法看到您的私有卡片。'}
          {filters.accessTab === 'restricted' &&
            '仅展示您创建或被指定为可访问成员的受限文档；他人看不到与您无关的受限文档。'}
        </p>

        <div className="mm-table-shell lit-mm-table-shell--stitch">
        <div className="mm-table-toolbar">
          <div className="lit-toolbar-inner">
            <div className="lit-toolbar-search-row">
              <div className="mm-search lit-mm-search-grow">
                <LimsNavIcon name="search" sizePx={18} className="mm-search-icon" />
                <input
                  type="search"
                  className="mm-search-input"
                  placeholder="搜索标题、内容、标签…"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitSearch();
                    }
                  }}
                  aria-label="搜索知识库"
                />
              </div>
              <button type="button" className="mm-btn-primary lit-toolbar-submit" onClick={commitSearch}>
                <LimsNavIcon name="search" sizePx={18} />
                搜索
              </button>
            </div>
            <div className="lit-toolbar-filters">
              <Select
                className="lit-toolbar-select"
                allowClear
                placeholder="全部分类"
                title="按分类筛选"
                aria-label="按分类筛选"
                value={filters.category || undefined}
                onChange={(v) => {
                  setFilters((prev) => ({ ...prev, category: (v as string) || '' }));
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                style={{ minWidth: 168, maxWidth: '100%' }}
                options={PRESET_CATEGORIES.map((c) => ({ label: c.label, value: c.value }))}
              />
              <Select
                className="lit-toolbar-select"
                allowClear
                placeholder="全部状态"
                aria-label="按状态筛选"
                value={filters.status || undefined}
                onChange={(v) => {
                  setFilters((prev) => ({ ...prev, status: (v as string) || '' }));
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                style={{ width: 140 }}
              >
                <Option value="draft">草稿</Option>
                <Option value="published">已发布</Option>
                <Option value="archived">已归档</Option>
              </Select>
              <Select
                className="lit-toolbar-select"
                value={`${filters.sortBy}-${filters.sortOrder}`}
                onChange={(value) => {
                  const [sortBy, sortOrder] = (value as string).split('-');
                  setFilters((prev) => ({
                    ...prev,
                    sortBy,
                    sortOrder: sortOrder as 'asc' | 'desc',
                  }));
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                style={{ minWidth: 158 }}
              >
                {SORT_OPTIONS.map((o) => (
                  <Option key={o.value} value={o.value}>
                    {o.label}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        </div>

        <div className="mm-sheet-scroll" style={{ padding: 0 }}>
          <div className="lit-lib-list-bg">
            <Spin spinning={loading}>
              <div className="lit-lib-card-grid">
                {!loading && knowledgeBases.length === 0 ? (
                  <Typography.Paragraph type="secondary" style={{ gridColumn: '1 / -1' }}>
                    {filters.accessTab === 'public' && '暂无公开文档，可点击右上角新建或调整筛选条件。'}
                    {filters.accessTab === 'private' && '暂无私有文档。您创建的「仅本人」文档会出现在此列表。'}
                    {filters.accessTab === 'restricted' &&
                      '暂无符合条件的受限文档，或您尚不在某篇文档的允许访问列表中。'}
                  </Typography.Paragraph>
                ) : null}
                {knowledgeBases.map((record) => {
                  const snippet = kbListCardSnippet(record);
                  const tagPreview = (record.tags || []).slice(0, 4);
                  const moreTags = record.tags && record.tags.length > 4 ? record.tags.length - 4 : 0;

                  const menuItems: MenuProps['items'] = [
                    {
                      key: 'view',
                      label: '查看',
                      onClick: () => navigate(`/knowledge-base/${record._id}`)
                    },
                    {
                      key: 'edit',
                      label: '编辑',
                      onClick: () => navigate(`/knowledge-base/${record._id}/edit`)
                    },
                    { type: 'divider' },
                    {
                      key: 'delete',
                      label: '删除',
                      danger: true,
                      onClick: () => handleDelete(record._id)
                    }
                  ];

                  return (
                    <article
                      key={record._id}
                      role="button"
                      tabIndex={0}
                      className="lit-lib-card"
                      onClick={(e) => {
                        const el = e.target as HTMLElement;
                        if (el.closest('.ant-dropdown-trigger')) return;
                        if (el.closest('.lit-lib-card__actions')) return;
                        if (el.closest('button')) return;
                        if (el.closest('a')) return;
                        navigate(`/knowledge-base/${record._id}`);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/knowledge-base/${record._id}`);
                        }
                      }}
                    >
                      <div className="lit-lib-card__top">
                        <div className="lit-lib-card__top-left">
                          {record.video?.key ? (
                            <LimsNavIcon name="smart_display" sizePx={18} className="text-primary flex-shrink-0" />
                          ) : null}
                          {kbStatusPill(record.status)}
                        </div>
                        <div className="lit-lib-card__top-right">
                          <span className="lit-lib-card__time">{formatKbRelativeTime(record.updatedAt)}</span>
                          <Dropdown
                            menu={{ items: menuItems }}
                            trigger={['click']}
                            placement="bottomRight"
                          >
                            <Button
                              type="text"
                              size="small"
                              icon={<MoreOutlined />}
                              title="更多"
                              onClick={(ev) => ev.stopPropagation()}
                            />
                          </Dropdown>
                        </div>
                      </div>
                      <h3 className="lit-lib-card__title">{record.title}</h3>
                      {snippet ? (
                        <p className="lit-lib-card__snippet">{snippet}</p>
                      ) : (
                        <p className="lit-lib-card__snippet">暂无摘要</p>
                      )}
                      {tagPreview.length > 0 ? (
                        <div className="kb-tag-row" style={{ marginBottom: '0.75rem' }}>
                          {tagPreview.map((tag) => (
                            <span key={tag} className="kb-tag">
                              {tag}
                            </span>
                          ))}
                          {moreTags > 0 ? <span className="kb-tag kb-tag--more">+{moreTags}</span> : null}
                        </div>
                      ) : null}
                      <div className="lit-lib-card__meta">
                        <span className="lit-lib-card__meta-item">
                          <LimsNavIcon name="folder" sizePx={16} />
                          {record.category || '未分类'}
                        </span>
                        <span className="lit-lib-card__meta-item">
                          <LimsNavIcon name="person" sizePx={16} />
                          {record.createdBy?.username ?? '—'}
                        </span>
                        <span className="lit-lib-card__meta-item">
                          <LimsNavIcon name="visibility" sizePx={16} />
                          阅读 {record.viewCount ?? 0}
                        </span>
                        <span className="lit-lib-card__meta-item">
                          <LimsNavIcon name="thumb_up" sizePx={16} />
                          点赞 {record.likeCount ?? 0}
                        </span>
                      </div>
                      <div className="lit-lib-card__actions" onClick={(ev) => ev.stopPropagation()}>
                        <Button
                          type="default"
                          size="small"
                          className="lit-stitch-btn-secondary"
                          onClick={() => navigate(`/knowledge-base/${record._id}`)}
                        >
                          打开文档
                        </Button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </Spin>
            <div className="lit-lib-pagination-wrap">
              <Pagination
                size="small"
                current={pagination.page}
                pageSize={pagination.limit}
                total={pagination.total}
                showSizeChanger
                pageSizeOptions={[6, 10, 20, 50]}
                disabled={loading}
                showTotal={(t) => `共 ${t} 条`}
                onChange={(page, pageSize) => {
                  setPagination((prev) => ({
                    ...prev,
                    page,
                    limit: pageSize || prev.limit,
                  }));
                }}
              />
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseList;
